import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import styles from './Home.module.css';
import TopChoices from './TopChoices';
import ArrowSend from './ArrowSend';
import Toast from './Toast';
import { TargetIcon, ClipboardIcon, SearchIcon, ChartIcon, BookIcon, CalculatorIcon } from './Icons';
import { useAutoSave } from '../hooks/useAutoSave';

const Home = () => {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();
  const [savedColleges, setSavedColleges] = useState([]);
  const [askInput, setAskInput] = useState("");
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  // Auto-save college list
  useAutoSave('myCollegeList', savedColleges);

  useEffect(() => {
    const loadSavedColleges = async () => {
      // First check for backup data (in case localStorage was cleared)
      let localList = JSON.parse(localStorage.getItem('myCollegeList')) || [];
      
      // If no data in localStorage, try to restore from backup
      if (localList.length === 0) {
        try {
          const backup = localStorage.getItem('quila_data_backup');
          if (backup) {
            const backupData = JSON.parse(backup);
            if (backupData.collegeList && backupData.collegeList.length > 0) {
              localList = backupData.collegeList;
              localStorage.setItem('myCollegeList', JSON.stringify(localList));
              console.log('[Home] Restored college list from backup');
            }
          }
        } catch (error) {
          console.error('Error restoring from backup:', error);
        }
      }
      
      setSavedColleges(localList);

      // Then try to sync from backend if authenticated (if backend is available)
      // Note: Express backend may not be deployed, so this might fail silently
      if (isAuthenticated && user) {
        try {
          const token = await getAccessTokenSilently();
          const { loadCollegeListFromBackend, syncCollegeList } = await import('../utils/collegeListSync');
          // Use relative URL - will go through Amplify proxy to backend
          const apiBaseUrl = process.env.REACT_APP_EXPRESS_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || '';
          
          const syncedList = await syncCollegeList(localList, token, apiBaseUrl);
          if (syncedList && syncedList.length !== localList.length) {
            setSavedColleges(syncedList);
          }
        } catch (error) {
          console.error('Error syncing college list (backend may not be available):', error);
          // Keep using localStorage version - this is expected if Express backend isn't deployed
        }
      }
    };

    loadSavedColleges();

    // Handle storage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'myCollegeList') {
        loadSavedColleges();
      }
    };

    // Handle custom events from the same tab
    const handleCollegeListUpdate = () => {
      loadSavedColleges();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('collegeListUpdated', handleCollegeListUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('collegeListUpdated', handleCollegeListUpdate);
    };
  }, [isAuthenticated, user, getAccessTokenSilently]);

  const handleCollegeSelect = (college) => {
    // Navigate to the college profile page
    navigate(`/college-profile/${encodeURIComponent(college.name)}`);
  };

  const handleRemoveCollege = (collegeToRemove) => {
    try {
      const updatedList = savedColleges.filter(c => c.name !== collegeToRemove.name);
      setSavedColleges(updatedList);
      // Update localStorage
      localStorage.setItem('myCollegeList', JSON.stringify(updatedList));
      // Notify other tabs/components
      window.dispatchEvent(new Event('collegeListUpdated'));
      // Show success toast
      setToast({
        message: `${collegeToRemove.name} removed from your list`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error removing college from list:', error);
      setToast({
        message: 'Error removing college from list',
        type: 'error'
      });
    }
  };

  const closeToast = () => {
    setToast(null);
  };

  if (isLoading) {
    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  return (
    <div className={styles.homeContainer}>
      <div className={styles.welcomeSection}>
        <h1 className={styles.welcomeTitle}>Welcome back!</h1>
        <p className={styles.welcomeSubtitle}>
          Ready to continue your college journey?
        </p>
      </div>

      <div className={styles.searchSection}>
        <form className={styles.homeAskBar} onSubmit={e => {
          e.preventDefault();
          if (askInput.trim()) navigate(`/chatbot?q=${encodeURIComponent(askInput)}`);
        }}>
          <input
            className={styles.homeAskInput}
            type="text"
            placeholder="Ask a question"
            value={askInput}
            onChange={e => setAskInput(e.target.value)}
          />
          <button className={styles.homeAskSend} type="submit" disabled={!askInput.trim()}>
            <ArrowSend />
          </button>
        </form>
        <div className={styles.homeQuickRepliesBar}>
          <button className={styles.homeQuickReplyPill} onClick={() => navigate(`/chatbot?q=${encodeURIComponent('Suggest colleges')}`)}>Suggest colleges</button>
          <button className={styles.homeQuickReplyPill} onClick={() => navigate(`/chatbot?q=${encodeURIComponent('Improve my chances')}`)}>Improve my chances</button>
          <button className={styles.homeQuickReplyPill} onClick={() => navigate(`/chatbot?q=${encodeURIComponent('Review my essay')}`)}>Review my essay</button>
          <button className={styles.homeQuickReplyPill} onClick={() => navigate(`/chatbot?q=${encodeURIComponent('Homework help')}`)}>
            Homework help <span className={styles.newBadge}>New</span>
          </button>
          <button className={styles.homeQuickReplyPill} onClick={() => navigate(`/chatbot?q=${encodeURIComponent('Brainstorm essays')}`)}>Brainstorm essays</button>
          <button className={styles.homeQuickReplyPill} onClick={() => navigate(`/chatbot?q=${encodeURIComponent('Paying for college')}`)}>Paying for college</button>
        </div>
      </div>

      <div className={styles.collegeSection}>
        <TopChoices
          colleges={savedColleges}
          onCollegeSelect={handleCollegeSelect}
          onRemoveCollege={handleRemoveCollege}
        />
      </div>

      <div className={styles.quickActions}>
        <h2 className={styles.sectionTitle}>Quick Actions</h2>
        <div className={styles.actionGrid}>
          <div 
            className={styles.actionCard}
            onClick={() => navigate('/compass')}
          >
            <div className={styles.actionIcon}>
              <TargetIcon size={48} color="#007AFF" />
            </div>
            <h3>Track Activities</h3>
            <p>Manage your extracurricular activities and achievements</p>
          </div>
          
          <div 
            className={styles.actionCard}
            onClick={() => navigate('/pathway-planner')}
          >
            <div className={styles.actionIcon}>
              <ClipboardIcon size={48} color="#007AFF" />
            </div>
            <h3>Set Goals</h3>
            <p>Plan your college application timeline and milestones</p>
          </div>
          
          <div 
            className={styles.actionCard}
            onClick={() => navigate('/explore')}
          >
            <div className={styles.actionIcon}>
              <SearchIcon size={48} color="#007AFF" />
            </div>
            <h3>Explore Colleges</h3>
            <p>Discover and research potential colleges</p>
          </div>
          
          <div 
            className={styles.actionCard}
            onClick={() => navigate('/analytics')}
          >
            <div className={styles.actionIcon}>
              <ChartIcon size={48} color="#007AFF" />
            </div>
            <h3>View Analytics</h3>
            <p>Analyze your college application chances</p>
          </div>

          <div 
            className={styles.actionCard}
            onClick={() => navigate('/resource-page')}
          >
            <div className={styles.actionIcon}>
              <BookIcon size={48} color="#007AFF" />
            </div>
            <h3>Resources</h3>
            <p>Access scholarships, internships, and study materials</p>
          </div>

          <div 
            className={styles.actionCard}
            onClick={() => navigate('/gpa-calculator')}
          >
            <div className={styles.actionIcon}>
              <CalculatorIcon size={48} color="#007AFF" />
            </div>
            <h3>GPA Calculator</h3>
            <p>Calculate and track your grade point average</p>
          </div>
        </div>
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default Home; 